<template>
  <div class="page-report">
    <div class="page-header align-items-start min-vh-100">
      <span class="mask bg-white"></span>
      <div class="container">
        <div class="row">
          <div class="col-12 mt-4">
            <div class="d-flex align-items-end flex-wrap gap-2 mb-3">
              <div>
                <h2 class="mb-0">{{ $t("rb_title") }}</h2>
                <p class="text-sm text-secondary mb-0">{{ $t("rb_intro") }}</p>
              </div>
              <div class="ms-auto">
                <material-button color="info" size="sm" @click="openEditor()">
                  {{ $t("rb_new_report") }}
                </material-button>
              </div>
            </div>

            <div v-if="loaded && reports.length" class="row g-3">
              <div v-for="(report, index) in reports" :key="report.id" class="col-lg-4 col-md-6 col-12">
                <div class="rb-card">
                  <h5 class="mb-1">{{ report.name }}</h5>
                  <div class="text-sm text-secondary mb-3">{{ describe(report.definition) }}</div>
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <material-button color="info" size="sm" @click="run(report)">{{ $t("rb_run") }}</material-button>
                    <material-button variant="outline" color="info" size="sm" @click="openEditor(report.id)">{{ $t("rb_edit") }}</material-button>
                    <button type="button" class="btn btn-link btn-sm mb-0 px-1" @click="duplicate(report)">{{ $t("rb_duplicate") }}</button>
                    <span class="ms-auto"></span>
                    <button type="button" class="rb-icon" :disabled="index === 0" :aria-label="$t('rb_move_up')" @click="move(index, -1)">▲</button>
                    <button type="button" class="rb-icon" :disabled="index === reports.length - 1" :aria-label="$t('rb_move_down')" @click="move(index, 1)">▼</button>
                    <button type="button" class="rb-icon" :aria-label="$t('rb_delete')" @click="askDelete(report)">✕</button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="loaded" class="rb-card text-center text-secondary">{{ $t("rb_no_reports") }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="deleting" class="rb-scrim" @click.self="deleting = null">
      <div class="rb-dialog">
        <h5>{{ $t("rb_delete_title", { name: deleting.name }) }}</h5>
        <p class="text-sm">{{ $t("rb_delete_body") }}</p>
        <div class="d-flex justify-content-end gap-2">
          <material-button variant="outline" color="secondary" size="sm" @click="deleting = null">{{ $t("rb_cancel") }}</material-button>
          <material-button color="danger" size="sm" @click="confirmDelete">{{ $t("rb_delete") }}</material-button>
        </div>
      </div>
    </div>

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="() => (snackbar = null)"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import AnalyticsService from "../../../services/analytics.service";
import { fieldMap } from "./reportBuilder";

export default {
  name: "ReportBuilderList",
  components: { MaterialButton, MaterialSnackbar, MaterialLoading },
  data() {
    return {
      meta: null,
      reports: [],
      loaded: false,
      deleting: null,
      loaderActive: false,
      snackbar: null,
      message: "",
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loaderActive = true;
      try {
        const [fields, list] = await Promise.all([
          AnalyticsService.reportBuilderFields(),
          AnalyticsService.reportBuilderList(),
        ]);
        if (fields.data.result.isSuccess) this.meta = fields.data.data;
        if (list.data.result.isSuccess) this.reports = list.data.data.reports;
        else this.notify(list.data.result.message, "danger");
      } catch (e) {
        this.notify(this.$t("rb_load_failed"), "danger");
      } finally {
        this.loaded = true;
        this.loaderActive = false;
      }
    },
    describe(definition) {
      if (!this.meta || !definition) return "";
      const fields = fieldMap(this.meta);
      const source = this.meta.sources[definition.source]?.label || "";
      const plain = definition.columns.filter((c) => !c.agg).map((c) => fields[c.field]?.label || c.field);
      const grouped = definition.columns.some((c) => c.agg);
      const by = grouped
        ? plain.length
          ? this.$t("rb_desc_by", { cols: plain.join(", ") })
          : this.$t("rb_desc_one_row")
        : this.$t("rb_desc_every_row");
      return `${source} · ${by} · ${this.$t("rb_desc_filters", { n: definition.filters.length })}`;
    },
    run(report) {
      this.$router.push({ name: "ReportBuilderView", params: { id: report.id } });
    },
    openEditor(id) {
      this.$router.push(id ? { name: "ReportBuilderEdit", params: { id } } : { name: "ReportBuilderNew" });
    },
    async duplicate(report) {
      const res = await AnalyticsService.reportBuilderSave({
        name: `${report.name} (copy)`.slice(0, 150),
        definition: report.definition,
        export_header: report.export_header,
      });
      if (res.data.result.isSuccess) {
        this.notify(this.$t("rb_duplicated"), "success");
        this.load();
      } else {
        this.notify(res.data.result.message, "danger");
      }
    },
    async move(index, step) {
      const list = [...this.reports];
      [list[index], list[index + step]] = [list[index + step], list[index]];
      this.reports = list;
      const res = await AnalyticsService.reportBuilderReorder(list.map((r) => r.id));
      if (!res.data.result.isSuccess) this.notify(res.data.result.message, "danger");
    },
    askDelete(report) {
      this.deleting = report;
    },
    async confirmDelete() {
      const report = this.deleting;
      this.deleting = null;
      const res = await AnalyticsService.reportBuilderDelete(report.id);
      if (res.data.result.isSuccess) {
        this.reports = this.reports.filter((r) => r.id !== report.id);
        this.notify(this.$t("rb_deleted"), "success");
      } else {
        this.notify(res.data.result.message, "danger");
      }
    },
    notify(message, type) {
      this.message = message;
      this.snackbar = type;
      setTimeout(() => (this.snackbar = null), 3000);
    },
  },
};
</script>

<style scoped>
.rb-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  height: 100%;
}
.rb-icon {
  border: 0;
  background: none;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 4px;
}
.rb-icon:hover:not(:disabled) {
  background: #f0f2f2;
}
.rb-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 16px;
}
.rb-dialog {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  width: min(460px, 100%);
}
</style>
